import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { useProfileId } from '@/features/org/hooks/use-profile-id';
import {
  addSubprojectComment,
  deleteSubprojectComment,
  getSubprojectComments,
  updateSubprojectCommentSeen,
} from '@/features/projects/create-subproject/api/subproject-comments';
import { ProjectType } from '@/shared/types/api';

type SubprojectCommentBaseParams = {
  clientViewId: number;
  contractorViewId: number;
  type: ProjectType;
};

export type SubprojectCommentParams = SubprojectCommentBaseParams;

export const subprojectCommentsQueryKey = (params: SubprojectCommentParams) => [
  'subproject-comments',
  params.type,
  params.clientViewId,
  params.contractorViewId,
];

export function useSubprojectComments(params: SubprojectCommentParams) {
  return useQuery({
    queryKey: subprojectCommentsQueryKey(params),
    queryFn: () => getSubprojectComments(params),
  });
}

export function useAddSubprojectComment(params: SubprojectCommentParams) {
  const queryClient = useQueryClient();
  const { data: profileId } = useProfileId();

  return useMutation({
    mutationFn: (message: string) => {
      if (!profileId) {
        throw new Error('Profile ID is required');
      }
      return addSubprojectComment(params, message, profileId);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: subprojectCommentsQueryKey(params),
      });
    },
  });
}

export function useDeleteSubprojectComment(params: SubprojectCommentParams) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: deleteSubprojectComment,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: subprojectCommentsQueryKey(params),
      });
    },
  });
}

export function useUpdateSubprojectCommentSeen(
  params: SubprojectCommentParams,
) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id }: { id: number }) => updateSubprojectCommentSeen(id),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: subprojectCommentsQueryKey(params),
      });
    },
  });
}
